import * as React from 'react';
import { useIntl } from 'react-intl';
import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { createColumnHelper } from '@tanstack/react-table';
import { commonMessages } from 'src/utils/messages';
import useSearch from 'src/registry/hooks/useSearch';
import { useTitleStore } from 'src/stores/useTitleStore';
import { useListAccountingRanges } from 'src/features/accountingRanges/api/useListAccountingRanges';
import { accountingRangesMessages } from 'src/features/accountingRanges/utils/messages';
import { AccountingRange } from 'src/features/accountingRanges/utils/types';
import { billingMessages } from 'src/features/billing/utils/messages';
import CreateAccountingRangeFormModal from 'src/features/accountingRanges/components/forms/CreateAccountingRangeFormModal';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import CellAccountingRangeActions from 'src/features/accountingRanges/components/tables/CellAccountingRangeActions';
import { booleanBadgeOptions } from 'src/utils/constants';
import usePagination from 'src/registry/hooks/usePagination';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const AccountingRangesTable = () => {
    const { formatMessage } = useIntl();
    const permissionManageAccountingRanges = useHasPermissions([PERMISSION.MANAGE_ACCOUNTING_RANGES]);
    const permissionAccessBillingEntities = useHasPermissions([PERMISSION.ACCESS_BILLING_ENTITIES]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(accountingRangesMessages.accountingRanges));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const {
        data: accountingRanges,
        isLoading,
        isError,
    } = useListAccountingRanges({
        ...querySearchOptions,
        ...queryPaginationOptions,
    });

    const columnHelper = createColumnHelper<AccountingRange>();

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            cell: (ctx) => <CellLinkWithIcon {...ctx} to={`/accounting-ranges/${ctx.row.original.id}`} />,
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        columnHelper.accessor('billing_entity.name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                const billingEntityId = ctx.row.original.billing_entity.id;
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        to={`/billing-entity/${billingEntityId}`}
                        hasPermissions={permissionAccessBillingEntities}
                    />
                );
            },
            meta: {
                title: formatMessage(billingMessages.billingEntity),
            },
        }),
        columnHelper.accessor('start', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(accountingRangesMessages.start),
            },
        }),
        columnHelper.accessor('end', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(accountingRangesMessages.end),
            },
        }),
        columnHelper.accessor('is_manual', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(accountingRangesMessages.isManual),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellAccountingRangeActions,
            maxSize: 45,
        }),
    ];
    return (
        <DataTable<AccountingRange, unknown>
            columns={columns}
            data={accountingRanges}
            semantics={{
                title: formatMessage(accountingRangesMessages.accountingRanges),
                entryName: formatMessage(accountingRangesMessages.range).toLowerCase(),
                entryNames: formatMessage(accountingRangesMessages.ranges).toLowerCase(),
                placeholder: formatMessage(accountingRangesMessages.accountingRange),
            }}
            search={search}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageAccountingRanges && { newEntry: CreateAccountingRangeFormModal }),
            }}
        />
    );
};

export default AccountingRangesTable;
